// The Stations screen's server state (spec/13 §12, F-ORG-03 #408, F-STN-21/-19), as TanStack Query
// hooks over the generated client — `packages/contracts/src/routes/{stations,station-classes,zones}.ts`:
//
//   GET   /v1/organizations/{o}/venues/{v}/stations               the venue's stations   stations:read
//   POST  /v1/organizations/{o}/venues/{v}/stations               define one             stations:manage
//   PATCH …/stations/{stationId}                                  edit one               stations:manage
//   GET   …/station-classes  POST …  PATCH …/{classId}            classes                stations:read/manage
//   GET   …/zones            POST …  PATCH …/{zoneId}             zones                  floor:read/arrange
//   GET   …/station-clients                                       each PC's client       stations:read
//   POST  …/stations/{stationId}/client-update/install-now        Update now             stations:maintenance
//   GET   …/station-installers                                    the MSIs to download   stations:read
//
// The Classes tab (F-STN-21) and the Zones tab (F-STN-19) create and edit through these; the
// add-a-station form fills its class and zone selects from the same class and zone lists.

import type {
  ApiClient,
  ApproveDevicePairingRequest,
  CreateStation,
  CreateStationClass,
  CreateZone,
  IdempotencyKey,
  StationClass,
  StationClientList,
  StationInstallers,
  UpdateStation,
  UpdateStationClass,
  UpdateZone,
  Zone,
} from '@clavian/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api/client';
import { floorKeys } from '../floor/queries';
import { anyClientMoving } from './client';

/** What Update now is for: install on this PC, or — on a diskless PC started as CCBoot super client — into the image. */
export type UpdateNowReason = 'install_now' | 'update_image';

/** A page of a list: the api's maximum, so a venue's stations, classes and zones are one page each. */
const PAGE_SIZE = 100;

export const stationKeys = {
  list: (organizationId: string, venueId: string) => ['stations', organizationId, venueId, 'list'] as const,
  classes: (organizationId: string, venueId: string) => ['stations', organizationId, venueId, 'classes'] as const,
  zones: (organizationId: string, venueId: string) => ['stations', organizationId, venueId, 'zones'] as const,
  clients: (organizationId: string, venueId: string) => ['stations', organizationId, venueId, 'clients'] as const,
  installers: (organizationId: string, venueId: string) => ['stations', organizationId, venueId, 'installers'] as const,
};

function listStationsPage(api: ApiClient, organizationId: string, venueId: string, cursor: string | undefined) {
  return api.listStations({
    path: { organizationId, venueId },
    query: { limit: PAGE_SIZE, ...(cursor === undefined ? {} : { cursor }) },
  });
}

/** The venue's stations, oldest first, a page at a time. */
export function useStations(organizationId: string, venueId: string) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: stationKeys.list(organizationId, venueId),
    queryFn: ({ pageParam }) => listStationsPage(api, organizationId, venueId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** The venue's station classes, for the add form's Class select (and to name a station's class). */
export function useStationClasses(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery<readonly StationClass[]>({
    queryKey: stationKeys.classes(organizationId, venueId),
    enabled,
    queryFn: async () => (await api.listStationClasses({ path: { organizationId, venueId }, query: { limit: PAGE_SIZE } })).data,
  });
}

/** The venue's zones, for the add form's Zone select (and to name a station's zone). */
export function useZones(organizationId: string, venueId: string) {
  const api = useApi();
  return useQuery<readonly Zone[]>({
    queryKey: stationKeys.zones(organizationId, venueId),
    queryFn: async () => (await api.listZones({ path: { organizationId, venueId }, query: { limit: PAGE_SIZE } })).data,
  });
}

/**
 * Each PC's station client (F-STN-12, spec/13 §3): the version it runs, the version it would be offered now, and
 * its updater's state — one request for the whole venue, joined to a station by `stationId` where it is shown (the
 * Stations list's Client cell, the floor drawer's section). The station reports only on change, so a slow refresh
 * is plenty — except while an update is moving on any PC of the venue (it reports its download's progress at most
 * every 5 seconds, spec/10 §8.3) or an Update now is being followed (`following`): then every 5 seconds, so the bar
 * and the phase move with it.
 */
export function useStationClients(organizationId: string, venueId: string, enabled = true, following = false) {
  const api = useApi();
  return useQuery<StationClientList>({
    queryKey: stationKeys.clients(organizationId, venueId),
    queryFn: () => api.listVenueStationClients({ path: { organizationId, venueId } }),
    enabled,
    staleTime: following ? 0 : 30_000,
    refetchInterval: (query) => (following || anyClientMoving(query.state.data) ? 5_000 : 60_000),
  });
}

/**
 * "Update now" (F-STN-12, spec/10 §8.2; `stations:maintenance`): ask one paired, online PC to check for the latest
 * release and install it as soon as no session runs. Answers the `station.client.update` command, which the caller
 * follows to the station's answer; the client list is re-read so its status starts moving at once.
 */
export function useUpdateStationNow(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    // `update_image` (2026-10-07): the PC is diskless and was started as CCBoot super client, so the update goes into the
    // image. Plain Update now sends no body, as before.
    mutationFn: ({ stationId, reason }: { readonly stationId: string; readonly reason: UpdateNowReason }) =>
      api.installStationClientUpdateNow({ path: { organizationId, venueId, stationId }, ...(reason === 'update_image' ? { body: { reason } } : {}) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: stationKeys.clients(organizationId, venueId) }),
  });
}

/**
 * The Clavian Station and Clavian Counter installers a new PC downloads (F-STN-01, F-STN-17): the release the venue's
 * channel settles on. Changes only when HQ publishes, so it is read once per visit.
 */
export function useStationInstallers(organizationId: string, venueId: string) {
  const api = useApi();
  return useQuery<StationInstallers>({
    queryKey: stationKeys.installers(organizationId, venueId),
    queryFn: () => api.getVenueStationInstallers({ path: { organizationId, venueId } }),
    staleTime: 10 * 60_000,
  });
}

/** Define a station, then re-read the list so the new one appears. */
export function useCreateStation(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateStation) => api.createStation({ path: { organizationId, venueId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: stationKeys.list(organizationId, venueId) }),
  });
}

/** Edit a station (F-ORG-03, `stations:manage`); only the fields sent change. Re-reads the list. */
export function useUpdateStation(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stationId, body }: { stationId: string; body: UpdateStation }) =>
      api.updateStation({ path: { organizationId, venueId, stationId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: stationKeys.list(organizationId, venueId) }),
  });
}

/** Create a station class (F-STN-21, `stations:manage`), then re-read the classes so it appears. */
export function useCreateStationClass(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateStationClass) => api.createStationClass({ path: { organizationId, venueId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: stationKeys.classes(organizationId, venueId) }),
  });
}

/** Edit a station class (F-STN-21, `stations:manage`); only the fields sent change. */
export function useUpdateStationClass(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ classId, body }: { classId: string; body: UpdateStationClass }) =>
      api.updateStationClass({ path: { organizationId, venueId, classId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: stationKeys.classes(organizationId, venueId) }),
  });
}

/** Create a zone (F-STN-19, `floor:arrange`), then re-read the zones so it appears. */
export function useCreateZone(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateZone) => api.createZone({ path: { organizationId, venueId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: stationKeys.zones(organizationId, venueId) }),
  });
}

/** Rename or recolour a zone (F-STN-19, `floor:arrange`). Its floor is fixed once created. */
export function useUpdateZone(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ zoneId, body }: { zoneId: string; body: UpdateZone }) =>
      api.updateZone({ path: { organizationId, venueId, zoneId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: stationKeys.zones(organizationId, venueId) }),
  });
}

/**
 * Approve a device pairing (F-STN-02, spec/07 §5): a staff member reads the 8-character code off the
 * PC and binds its device to a station. `stations:manage`, org-scoped
 * (`POST /v1/organizations/{o}/device-pairings/{code}/approve`). On success re-read the list so the
 * station shows its new bound device.
 */
export function useApproveDevicePairing(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, body }: { code: string; body: ApproveDevicePairingRequest }) =>
      api.approveDevicePairing({ path: { organizationId, code }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: stationKeys.list(organizationId, venueId) }),
  });
}

/** What a station is unpaired or removed with, and the one `Idempotency-Key` for that confirm (a retry reuses it). */
export interface StationChangeVars {
  readonly stationId: string;
  readonly idempotencyKey: string;
}

/**
 * Re-read everything keyed on the venue's stations after one is unpaired or removed (ADR-0057): the Stations list and
 * clients, and the floor snapshot, plan and figures (all under the floor's key).
 */
function invalidateStations(queryClient: ReturnType<typeof useQueryClient>, organizationId: string, venueId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['stations', organizationId, venueId] }),
    queryClient.invalidateQueries({ queryKey: floorKeys.snapshot(organizationId, venueId) }),
  ]);
}

/** Unpair a station's PC (ADR-0057, `stations:manage`): the station waits for a PC to be paired. */
export function useUnpairStation(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stationId, idempotencyKey }: StationChangeVars) =>
      api.unpairStation({ path: { organizationId, venueId, stationId }, headerParams: { 'idempotency-key': idempotencyKey as IdempotencyKey } }),
    onSuccess: () => invalidateStations(queryClient, organizationId, venueId),
  });
}

/** Remove a station (ADR-0057, `stations:manage`): it leaves the floor and the list, and its number is free. */
export function useRemoveStation(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stationId, idempotencyKey }: StationChangeVars) =>
      api.removeStation({ path: { organizationId, venueId, stationId }, headerParams: { 'idempotency-key': idempotencyKey as IdempotencyKey } }),
    onSuccess: () => invalidateStations(queryClient, organizationId, venueId),
  });
}
